
import { create } from 'zustand';
import { getUserGymId } from '@/utils/gymAuth';

interface Category {
  id: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
}

interface CategoryStore {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
  addCategory: (name: string) => Promise<Category | null>;
  updateCategory: (id: string, name: string) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<boolean>;
}

export const useCategoryStore = create<CategoryStore>((set) => ({
  categories: [],
  isLoading: false,
  error: null,

  fetchCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      const { getCategories } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
      const data = await getCategories(gymId);
      set({ categories: data || [] });
    } catch (error) {
      console.error('Error fetching categories:', error);
      set({ error: 'Failed to fetch categories' });
    } finally {
      set({ isLoading: false });
    }
  },

  addCategory: async (name: string) => {
    try {
      const gymId = getUserGymId();
      const { createCategory } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
      const data = await createCategory(name, gymId);
      if (!data) return null;

      set(state => ({
        categories: [...state.categories, data]
      }));

      return data;
    } catch (error) {
      console.error('Error adding category:', error);
      return null;
    }
  },

  updateCategory: async (id: string, name: string) => {
    try {
      const gymId = getUserGymId();
      const { updateCategory } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
      await updateCategory(id, name, gymId);

      set(state => ({
        categories: state.categories.map(cat =>
          cat.id === id ? { ...cat, name } : cat
        )
      }));

      return true;
    } catch (error) {
      console.error('Error updating category:', error);
      return false;
    }
  },

  deleteCategory: async (id: string) => {
    try {
      const { deleteCategory } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
      await deleteCategory(id);

      set(state => ({
        categories: state.categories.filter(cat => cat.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting category:', error);
      return false;
    }
  }
}));
